import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { useLocation, useNavigate } from "react-router-dom";
import { addAttendeesToEvent } from "../../services/eventService";
import { InputNumber } from "primereact/inputnumber";
import { FloatLabel } from "primereact/floatlabel";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

interface AddAttendeeFormProps {
  eventId: number;
  availableSeats: number;
  eventDetails: {
    title: string;
  };
  onRegistered: () => void;
}

const AddAttendeeForm: React.FC<AddAttendeeFormProps> = ({
  eventId,
  availableSeats,
  eventDetails,
  onRegistered,
}) => {
  const { showSuccessToast, showErrorToast } = useToast();

  const { isAuthenticated, setRedirectUrl } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const onHide = () => {
    setVisible(false);
  };

  const onShow = () => {
    if (!eventId) return;
    if (availableSeats < 1) return;

    if (!isAuthenticated) {
      showErrorToast("Please login to add attendees.");

      setRedirectUrl(location.pathname);

      navigate("/login"); // Redirect to login page if not authenticated
    } else {
      setVisible(true);
    }
  };

  const [loading, setLoading] = useState(false);

  const addAttendees = async () => {
    try {
      if (!eventId) return;

      if (numberOfAttendees < 1) {
        showErrorToast("Please enter a valid number of attendees.");
        return;
      }

      var payload = {
        manual_attendee_name: attendeeName.trim(),
        manual_attendee_email: attendeeEmail.trim(),
        number_of_attendees: numberOfAttendees,
      };
      setLoading(true);
      const res = await addAttendeesToEvent(eventId, payload);
      showSuccessToast(res.data.message);

      handleSuccess();
    } catch (error) {
      showErrorToast("Failed to add attendees. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const [visible, setVisible] = useState(false);
  const handleSuccess = () => {
    onHide(); // Close the dialog on successful payment
    onRegistered(); // Call the onRegistered function to update the parent component
  };

  const [attendeeName, setAttendeeName] = useState("");
  const [attendeeEmail, setAttendeeEmail] = useState("");
  const [numberOfAttendees, setNumberOfAttendees] = useState(1);

  const maxMembers = availableSeats > 0 ? availableSeats : 1;

  return (
    <>
      <a className="text-decoration-underline" onClick={onShow}>
        Register Attendees
      </a>
      <Dialog
        header="Register Attendees on Their Behalf"
        visible={visible}
        onHide={onHide}
        draggable={false}
        style={{ width: "32rem" }}
        className="p-fluid"
      >
        <div>
          <span className="head">
            <strong>Event:</strong> {eventDetails.title}
          </span>
          <div className="col-12 d-flex align-items-center justify-content-center mt-3">
            <p>
              Manually register attendees for this event. Please ensure you have
              their consent and accurate information before proceeding.
            </p>
          </div>
          <div className="col-12 align-items-center justify-content-center">
            <div className="contact-one__input-box">
              <FloatLabel>
                <label htmlFor="attendee_name">
                  <span className="text-danger">*</span> Attendee Full Name
                </label>
                <InputText
                  id="attendee_name"
                  value={attendeeName}
                  onChange={(e) => setAttendeeName(e.target.value)}
                  maxLength={100}
                />
              </FloatLabel>
            </div>
            <div className="contact-one__input-box">
              <FloatLabel>
                <label htmlFor="attendee_email">
                  <span className="text-danger">*</span> Email for confirmation
                  & event updates
                </label>
                <InputText
                  id="attendee_email"
                  keyfilter="email"
                  value={attendeeEmail}
                  onChange={(e) => setAttendeeEmail(e.target.value)}
                  maxLength={100}
                />
              </FloatLabel>
            </div>
            <div className="contact-one__input-box mt-5">
              <FloatLabel>
                <InputNumber
                  value={numberOfAttendees}
                  onValueChange={(e) => setNumberOfAttendees(e.value || 1)}
                  className="w-full"
                  min={1}
                  max={maxMembers}
                  maxFractionDigits={0}
                  showButtons
                />
                <label htmlFor="number-input">
                  Number of Attendees (up to {maxMembers})
                </label>
              </FloatLabel>
            </div>
          </div>
          <div className="col-12 d-flex align-items-center justify-content-center mt-3">
            <Button
              label={loading ? "Registering..." : "Register Attendees"}
              outlined
              onClick={() => addAttendees()}
              className="thm-btn small_btn"
              disabled={
                loading ||
                !attendeeName.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendeeEmail) ||
                numberOfAttendees < 1
              }
            />
          </div>
          <div className="col-12 d-flex align-items-center justify-content-center mt-3">
            <p className="text-sm text-muted">
              <strong>Note:</strong> This registration will be linked to your
              account and recorded as a <strong>$0 manual entry</strong>. No
              payment will be processed.
            </p>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddAttendeeForm;
